import { useMutation, useQueryClient } from "@tanstack/react-query"
import { memo, useState } from "react"
import { Controller, type SubmitHandler, useForm } from "react-hook-form"
import { FaExchangeAlt } from "react-icons/fa"

import type { ApiError, UserResponse } from "@/client"
import { Checkbox } from "@/components/atoms/Checkbox"
import { FormDialog } from "@/components/molecules/FormDialog"
import { FormField } from "@/components/molecules/FormField"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"
import { emailPattern, handleError } from "@/utils"
import { Button, Flex, Input, VStack } from "@chakra-ui/react"
import { Field } from "@chakra-ui/react"

interface EditUserProps {
  user: UserResponse
}

interface UserUpdateForm {
  email: string
  full_name?: string
  password?: string
  confirm_password?: string
  is_active: boolean
}

const EditUser = memo(({ user }: EditUserProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const form = useForm<UserUpdateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: user,
  })

  const mutation = useMutation({
    mutationFn: (data: Omit<UserUpdateForm, "confirm_password">) =>
      api.admin.updateUserApiV1AdminUsersUserIdPut({
        userId: user.id,
        requestBody: data,
      }),
    onSuccess: () => {
      showSuccessToast("User updated successfully.")
      form.reset()
      setIsOpen(false)
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const onSubmit: SubmitHandler<UserUpdateForm> = async (data) => {
    if (data.password === "") {
      data.password = undefined
    }
    // Remove confirm_password before sending to API
    const { confirm_password, ...apiData } = data
    mutation.mutate(apiData)
  }

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setIsOpen(true)}>
        <FaExchangeAlt fontSize="16px" />
        Edit User
      </Button>
      <FormDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Edit User"
        onSubmit={form.handleSubmit(onSubmit)}
        submitText="Save"
        isLoading={mutation.isPending}
      >
        <VStack gap={4}>
          <FormField
            required
            isInvalid={!!form.formState.errors.email}
            errorText={form.formState.errors.email?.message}
            label="Email"
          >
            <Input
              id="email"
              {...form.register("email", {
                required: "Email is required",
                pattern: emailPattern,
              })}
              placeholder="Email"
              type="email"
            />
          </FormField>

          <FormField
            isInvalid={!!form.formState.errors.full_name}
            errorText={form.formState.errors.full_name?.message}
            label="Full Name"
          >
            <Input
              id="name"
              {...form.register("full_name")}
              placeholder="Full name"
              type="text"
            />
          </FormField>

          <FormField
            isInvalid={!!form.formState.errors.password}
            errorText={form.formState.errors.password?.message}
            label="Set Password"
          >
            <Input
              id="password"
              {...form.register("password", {
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
              })}
              placeholder="Password"
              type="password"
            />
          </FormField>

          <FormField
            isInvalid={!!form.formState.errors.confirm_password}
            errorText={form.formState.errors.confirm_password?.message}
            label="Confirm Password"
          >
            <Input
              id="confirm_password"
              {...form.register("confirm_password", {
                validate: (value) =>
                  value === form.getValues().password ||
                  "The passwords do not match",
              })}
              placeholder="Password"
              type="password"
            />
          </FormField>

          <Flex mt={4} direction="column" gap={4}>
            <Controller
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <Field.Root disabled={field.disabled}>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={({ checked }: { checked: boolean }) =>
                      field.onChange(checked)
                    }
                  >
                    Is active?
                  </Checkbox>
                </Field.Root>
              )}
            />
          </Flex>
        </VStack>
      </FormDialog>
    </>
  )
})

EditUser.displayName = "EditUser"

export default EditUser
